import {
  IDriveContent,
  IDriveContentAncestors,
  type IDrivePublicContent,
} from 'src/@types/drive';
import { API_HOST } from 'src/config-global';
import { uploadWorker } from 'src/services/FileUploader/uploadWorker';
import { axios } from 'src/utils/axios';

export type GetContentParams = {
  parent?: string;
  starred?: boolean;
  signedUrl?: boolean;
  sharedOnly?: boolean;
};

export async function getContents(
  params?: GetContentParams,
): Promise<IDriveContent[]> {
  const queryParams = new URLSearchParams();
  if (params?.parent) {
    queryParams.append('parent', params.parent);
  }
  if (params?.starred !== undefined) {
    queryParams.append('starred', String(params.starred));
  }
  if (params?.signedUrl !== undefined) {
    queryParams.append('signedUrl', String(params.signedUrl));
  }
  if (params?.sharedOnly !== undefined) {
    queryParams.append('sharedOnly', String(params.sharedOnly));
  }

  const { data } = await axios.get<IDriveContent[]>(
    `/drive?${queryParams.toString()}`,
  );
  return data;
}

export async function generatePublicAccessToken(
  id: string,
): Promise<{ token: { token: string } }> {
  const { data } = await axios.put<{ token: { token: string } }>(
    `/drive/generate-public-access-token`,
    {
      id,
    },
  );

  return data;
}

export async function revokePublicAccessToken(
  id: string,
): Promise<IDriveContent> {
  const { data } = await axios.post<IDriveContent>(
    `/drive/revoke-public-access-token`,
    {
      id,
    },
  );

  return data;
}

export async function getDownloadContentByToken(
  token: string,
): Promise<{ url: string }> {
  const { data } = await axios.post<{ url: string }>(
    `/drive/download-content-by-token?token=${token}`,
  );
  return data;
}

export async function getContentByToken(
  token: string,
): Promise<IDrivePublicContent> {
  const { data } = await axios.get<IDrivePublicContent>(
    `/drive/get-content-drive/${token}`,
  );
  return data;
}

export type DriveCreateFolderPayload = {
  name: string;
  parent?: string;
};
export async function createFolder({
  name,
  parent,
}: DriveCreateFolderPayload): Promise<IDriveContent> {
  const { data } = await axios.post<IDriveContent>(`/drive/create-folder`, {
    name,
    parent,
  });

  return data;
}

export type DriveRenameFolderPayload = {
  id: string;
  name: string;
  parent?: string;
};
export async function renameContent({
  id,
  name,
  parent,
}: DriveRenameFolderPayload): Promise<IDriveContent> {
  const { data } = await axios.put<IDriveContent>(`/drive/rename-content`, {
    id,
    name,
    parent,
  });

  return data;
}

export type DriveAddFilePayload = {
  file: File;
  parent?: string;
  onUploadProgress?: (_status: { speed: number; progress: number }) => void;
};
export async function addFile({
  file,
  parent,
  onUploadProgress,
}: DriveAddFilePayload): Promise<IDriveContent> {
  return uploadWorker<IDriveContent>(file, {
    extraStartPayload: {
      parent,
    },
    onProgress: (data) => {
      onUploadProgress?.(data);
    },
    endpoints: {
      start: `${API_HOST}/drive/multipart/start/add-file?parent=${parent}`,
      getSignedUrls: `${API_HOST}/drive/multipart/get-signed-urls/add-file`,
      finalize: `${API_HOST}/drive/multipart/finalize/add-file`,
    },
  });
}
export async function getDownloadUrl(id: string): Promise<{ url: string }> {
  const { data } = await axios.post<{ url: string }>(
    `/drive/download?id=${id}`,
  );

  return data;
}

export async function deleteContent(id: string): Promise<IDriveContent> {
  const { data } = await axios.delete<IDriveContent>(`/drive/delete/${id}`);
  return data;
}

export async function deleteContentPermanently(
  id: string,
): Promise<IDriveContent> {
  const { data } = await axios.delete<IDriveContent>(
    `/drive/delete-permanently/${id}`,
  );
  return data;
}

export async function getTrashFiles(q: string): Promise<IDriveContent[]> {
  const { data } = await axios.get<IDriveContent[]>(`/drive/trash`, {
    params: q ? { q } : null,
  });
  return data;
}

export async function restoreTrashFiles(id: string): Promise<IDriveContent> {
  const { data } = await axios.put<IDriveContent>(`/drive/restore/${id}`);
  return data;
}

export async function getContentAncestors(
  id: string,
): Promise<IDriveContentAncestors> {
  const { data } = await axios.get<IDriveContentAncestors>(
    `/drive/ancestors/${id}`,
  );
  return data;
}

export type DriveInfoPayload = {
  myDrive: DriveInfo;
  workspaceDrive: DriveInfo | null;
};

type DriveInfo = {
  _id: string;
  createdAt: string;
  updatedAt: string;
  totalSpace: number;
  usedSpace: number;
  user: string;
  workspace: string;
};

export async function getDriveInfo(): Promise<DriveInfoPayload> {
  const { data } = await axios.get<DriveInfoPayload>(`/drive/info`);
  return data;
}

export type DriveUpdateFavoritePayload = {
  id: string;
  isFavorited: boolean;
};
export async function updateFavorite({
  id,
  isFavorited,
}: DriveUpdateFavoritePayload): Promise<IDriveContent> {
  const { data } = await axios.patch<IDriveContent>(`/drive/favorite`, {
    id,
    isFavorited,
  });
  return data;
}

export async function searchContent(q: string): Promise<IDriveContent[]> {
  const { data } = await axios.get<IDriveContent[]>(`/drive/search?q=${q}`);
  return data;
}

type GetFilePayload = {
  contentRef: IDriveContent;
  publicToken: string;
};

export async function getFileTokenById(id: string): Promise<GetFilePayload> {
  const { data } = await axios.get<GetFilePayload>(`/drive/file/${id}`);
  return data;
}

export type DriveMoveContent = {
  contentIds: string[];
  parentId: string;
};

export async function MoveContentToParent({
  contentIds,
  parentId,
}: DriveMoveContent): Promise<IDriveContent> {
  const { data } = await axios.patch<IDriveContent>(`/drive/move`, {
    contentIds,
    parentId,
  });
  return data;
}

export type MutateParticipantContentPayload = {
  id: string;
  participants: string[];
};

export async function AddContentParticipant({
  id,
  participants,
}: MutateParticipantContentPayload): Promise<IDriveContent> {
  const { data } = await axios.put<IDriveContent>(`/drive/participant/add`, {
    id,
    participants,
  });
  return data;
}

export async function removeContentParticipant({
  id,
  participants,
}: MutateParticipantContentPayload): Promise<IDriveContent> {
  const { data } = await axios.put<IDriveContent>(`/drive/participant/remove`, {
    id,
    participants,
  });
  return data;
}

export * as DriveRequests from './drive';
